import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, Chip, Button,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, Divider, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  Search as SearchIcon, ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon, AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { browseClients, browseClientDetail } from '../services/api';
import PageSearchBar from '../components/PageSearchBar';

function fmtCurrency(val) {
  if (val == null) return '$0';
  const num = parseFloat(val);
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ClientBrowse() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('balance');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    browseClients()
      .then(res => setClients(res.data.clients || []))
      .catch(err => setError(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const filtered = clients.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.client_name || '').toLowerCase().includes(s) || (c.ar_code || '').toLowerCase().includes(s);
  });

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortBy], bv = b[sortBy];
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleViewClient = async (arCode) => {
    setSelectedClient(arCode);
    setDetailLoading(true);
    setClientDetail(null);
    try {
      const res = await browseClientDetail(arCode);
      setClientDetail(res.data);
    } catch (err) {
      setClientDetail({ error: err.response?.data?.error || 'Failed to load' });
    } finally {
      setDetailLoading(false);
    }
  };

  const totalOwed = clients.filter(c => c.balance > 0).reduce((s, c) => s + c.balance, 0);
  const totalClients = clients.filter(c => c.balance > 0).length;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Client Portal Preview</Typography>
          <Typography variant="body2" color="text.secondary">
            See exactly what each client sees — live from Sage AR
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Paper sx={{ p: 2, textAlign: 'center', minWidth: 140 }}>
            <Typography variant="h5" fontWeight="bold" color="error.main">{fmtCurrency(totalOwed)}</Typography>
            <Typography variant="caption" color="text.secondary">Total Open AR</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center', minWidth: 100 }}>
            <Typography variant="h5" fontWeight="bold" color="primary.main">{totalClients}</Typography>
            <Typography variant="caption" color="text.secondary">Clients w/ Balance</Typography>
          </Paper>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <PageSearchBar
          placeholder="Search by client name or AR code..."
          value={search}
          onChange={(v) => setSearch(v)}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              {[
                { key: 'ar_code', label: 'AR Code' },
                { key: 'client_name', label: 'Client Name' },
                { key: 'balance', label: 'Balance Due', align: 'right' },
                { key: 'job_count', label: 'Jobs', align: 'center' },
                { key: 'invoice_count', label: 'Invoices', align: 'center' },
                { key: 'last_activity', label: 'Last Activity' },
              ].map(col => (
                <TableCell key={col.key} align={col.align || 'left'} sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === col.key}
                    direction={sortBy === col.key ? sortDir : 'asc'}
                    onClick={() => handleSort(col.key)}
                  >{col.label}</TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>View</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map(c => (
              <TableRow key={c.ar_code} hover sx={{ cursor: 'pointer' }} onClick={() => handleViewClient(c.ar_code)}>
                <TableCell><Chip label={c.ar_code} size="small" variant="outlined" /></TableCell>
                <TableCell sx={{ fontWeight: 'medium' }}>{c.client_name}</TableCell>
                <TableCell align="right" sx={{
                  fontWeight: 'bold',
                  color: c.balance > 0 ? 'error.main' : c.balance < 0 ? 'success.main' : 'text.secondary',
                }}>{fmtCurrency(c.balance)}</TableCell>
                <TableCell align="center">{c.job_count}</TableCell>
                <TableCell align="center">{c.invoice_count}</TableCell>
                <TableCell>{fmtDate(c.last_activity)}</TableCell>
                <TableCell align="center">
                  <Button size="small" startIcon={<OpenInNewIcon />} onClick={(e) => { e.stopPropagation(); handleViewClient(c.ar_code); }}>
                    Preview
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {sorted.length} clients shown • Data live from Sage AR
      </Typography>

      {/* Client Detail Dialog */}
      <Dialog open={!!selectedClient} onClose={() => setSelectedClient(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0D2C54', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ color: '#fff' }}>
              <AccountBalanceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {clientDetail?.client_name || selectedClient}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Account {selectedClient} {clientDetail?.contact ? `• ${clientDetail.contact}` : ''} {clientDetail?.email ? `• ${clientDetail.email}` : ''}
            </Typography>
          </Box>
          {clientDetail?.total_balance_due > 0 && (
            <Chip label={fmtCurrency(clientDetail.total_balance_due)} color="error" sx={{ fontWeight: 'bold', fontSize: '1rem' }} />
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {detailLoading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
          {clientDetail?.error && <Alert severity="error">{clientDetail.error}</Alert>}
          {clientDetail && !clientDetail.error && !detailLoading && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                This is exactly what the client sees on their portal
              </Typography>
              <Divider sx={{ my: 2 }} />

              {clientDetail.jobs?.length === 0 && (
                <Typography color="text.secondary" sx={{ py: 2 }}>No open invoices — account is current.</Typography>
              )}

              {clientDetail.jobs?.map(job => (
                <Accordion key={job.job_number} defaultExpanded={job.ar_balance > 0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                      <Box>
                        <Typography fontWeight="bold">{job.description}</Typography>
                        <Typography variant="caption" color="text.secondary">Job #{job.job_number}</Typography>
                      </Box>
                      <Typography fontWeight="bold" color={job.ar_balance > 0 ? 'error.main' : 'success.main'}>
                        {fmtCurrency(job.ar_balance)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                      <Box><Typography variant="caption" color="text.secondary">Contract</Typography><Typography fontWeight="bold">{fmtCurrency(job.contract_amount)}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Billed</Typography><Typography fontWeight="bold">{fmtCurrency(job.total_billed)}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Paid</Typography><Typography fontWeight="bold" color="success.main">{fmtCurrency(job.total_paid)}</Typography></Box>
                    </Box>
                    {job.open_invoices?.length > 0 && (
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Invoice #</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Due Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Balance</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {job.open_invoices.map(inv => (
                            <TableRow key={inv.invoice_number}>
                              <TableCell>#{inv.invoice_number}</TableCell>
                              <TableCell>{fmtDate(inv.due_date)}</TableCell>
                              <TableCell>{inv.description || '—'}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>{fmtCurrency(inv.balance)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedClient(null)}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={() => { const w = window.open('/portal/login', '_blank'); if (!w) window.location.href = '/portal/login'; }}
          >
            Open Client Portal
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
